import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  VStack,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const chargesSchema = Yup.object().shape({
  chargeAmount: Yup.number()
    .required('Amount is required')
    .min(0.01, 'Amount must be greater than 0')
    .test(
      'is-decimal',
      'Amount cannot have more than 2 decimal places',
      (value) => {
        if (!value) return true;
        return /^\d+(\.\d{0,2})?$/.test(value.toString());
      }
    ),
  chargeId: Yup.string().when('$isEdit', {
    is: true,
    then: () => Yup.string().optional(),
    otherwise: () => Yup.string().required('Charge ID is required')
  }),
  chargeDate: Yup.string().required('Charge Date is required'),
  notes: Yup.string()
    .required('Notes are required')
    .min(5, 'Notes must be at least 5 characters'),
});

const ChargesModal = ({ isOpen, onClose, onSubmit, loading, selectedCharge = null }) => {
  console.log("🚀 ~ ChargesModal ~ selectedCharge:", selectedCharge)
  // Format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Handle ISO date string
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // Handle ISO date string
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDateChange = (date, fieldName) => {
    if (!date) {
      formik.setFieldValue(fieldName, '');
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    formik.setFieldValue(fieldName, `${year}-${month}-${day}`);
  };

  const formik = useFormik({
    initialValues: {
      chargeAmount: selectedCharge?.amount ? parseFloat(selectedCharge.amount.replace('$', '')) : '',
      chargeId: selectedCharge?.id || '',
      chargeDate: selectedCharge ? formatDateForInput(selectedCharge.date) : '',
      notes: selectedCharge?.notes || '',
    },
    validationSchema: chargesSchema,
    validationContext: { isEdit: !!selectedCharge },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await onSubmit({
          ...values,
          chargeAmount: Number(values.chargeAmount),
        });
        formik.resetForm();
        onClose();
      } catch (error) {
        throw error;
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <form onSubmit={formik.handleSubmit}>
          <ModalHeader>
            {selectedCharge ? 'Edit Charge' : 'Add Charge'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack spacing={4} width="100%">
                <FormControl 
                  isRequired 
                  isInvalid={formik.touched.chargeAmount && formik.errors.chargeAmount}
                >
                  <FormLabel>Amount</FormLabel>
                  <Input
                    name="chargeAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter amount"
                    value={formik.values.chargeAmount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>
                    {formik.errors.chargeAmount}
                  </FormErrorMessage>
                </FormControl>

                {!selectedCharge && (
                  <FormControl 
                    isRequired
                    isInvalid={formik.touched.chargeId && formik.errors.chargeId}
                  >
                    <FormLabel>Charge ID</FormLabel>
                    <Input
                      name="chargeId"
                      placeholder="Enter charge ID"
                      value={formik.values.chargeId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>
                      {formik.errors.chargeId}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </HStack>

              <FormControl 
                isRequired
                isInvalid={formik.touched.chargeDate && formik.errors.chargeDate}
              >
                <FormLabel>Charge Date</FormLabel>
                <Popover>
                  <PopoverTrigger>
                    <Input
                      name="chargeDate"
                      value={formatDateForDisplay(formik.values.chargeDate)}
                      placeholder="dd/mm/yyyy"
                      readOnly
                      onClick={(e) => e.target.blur()}
                    />
                  </PopoverTrigger>
                  <PopoverContent width="auto" p={0}>
                    <PopoverBody>
                      <Calendar
                        onChange={(date) => handleDateChange(date, 'chargeDate')}
                        value={formik.values.chargeDate ? new Date(formik.values.chargeDate) : null}
                        locale="en-GB"
                        maxDate={new Date()}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                <FormErrorMessage>
                  {formik.errors.chargeDate}
                </FormErrorMessage>
              </FormControl>

              <FormControl 
                isRequired
                isInvalid={formik.touched.notes && formik.errors.notes}
              >
                <FormLabel>Notes</FormLabel>
                <Textarea
                  name="notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter reason for charges..."
                  rows={4}
                />
                <FormErrorMessage>
                  {formik.errors.notes}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              type="submit"
              isLoading={loading}
              loadingText={selectedCharge ? "Updating Charge" : "Adding Charge"}
              isDisabled={!formik.isValid || formik.isSubmitting}
            >
              {selectedCharge ? 'Update Charge' : 'Add Charge'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ChargesModal;
